"use client";

import type React from "react";
import FormControl, {
  type FormControlMessageProps,
  type FormControlProps,
} from "src/components/form/FormControl/FormControl";
import Label from "src/components/form/Label/Label";
import TextArea, { type TextAreaProps } from "src/components/form/TextArea/TextArea";
import TextInput, { type TextInputProps } from "src/components/form/TextInput/TextInput";

type FieldOwnedProps =
  | "className"
  | "defaultValue"
  | "disabled"
  | "error"
  | "id"
  | "name"
  | "onValueProcess"
  | "required"
  | "value";

export type TextFieldProps = Omit<FormControlProps, "children"> &
  Omit<TextInputProps, FieldOwnedProps | "ref"> & {
    controlClassName?: string;
    hideLabel?: boolean;
    inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
    message?: React.ReactNode;
    messageProps?: FormControlMessageProps;
    rows?: number;
  };

const TextField = (props: TextFieldProps) => {
  const {
    name,
    id,
    className,
    controlClassName,
    defaultValue,
    value,
    disabled,
    hideLabel,
    onValueProcess,
    label,
    error,
    validate,
    inputRef,
    required,
    message,
    messageProps,
    rows,
    type,
    ...inputProps
  } = props;

  return (
    <FormControl
      name={name}
      id={id}
      className={className}
      defaultValue={defaultValue}
      value={value}
      disabled={disabled}
      onValueProcess={onValueProcess}
      label={label}
      error={error}
      validate={validate}
      required={required}
    >
      {hideLabel || !label ? null : <Label />}
      <FormControl.Input>
        {rows === undefined ? (
          <TextInput
            {...inputProps}
            aria-label={
              inputProps["aria-label"] ??
              (hideLabel && typeof label === "string" ? label : undefined)
            }
            ref={inputRef as React.Ref<HTMLInputElement>}
            className={controlClassName}
            type={type}
          />
        ) : (
          <TextArea
            {...(inputProps as unknown as TextAreaProps)}
            aria-label={
              inputProps["aria-label"] ??
              (hideLabel && typeof label === "string" ? label : undefined)
            }
            ref={inputRef as React.Ref<HTMLTextAreaElement>}
            className={controlClassName}
            rows={rows}
          />
        )}
      </FormControl.Input>
      <FormControl.Message {...messageProps}>{message}</FormControl.Message>
    </FormControl>
  );
};

export default TextField;
